import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
}

function simplifyFrac(num, den) {
  if (den === 0) return [num, den];
  let n = num;
  let d = den;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return [n / g, d / g];
}

function fracToMixedLatex(num, den) {
  const [n, d] = simplifyFrac(num, den);
  if (d === 1) return `${n}`;
  const sign = n < 0 ? "-" : "";
  const absN = Math.abs(n);
  const whole = Math.floor(absN / d);
  const rem = absN % d;
  if (whole === 0) return `${sign}\\dfrac{${absN}}{${d}}`;
  if (rem === 0) return `${sign}${whole}`;
  return `${sign}${whole}\\dfrac{${rem}}{${d}}`;
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

function term(coeff, symbol, isFirst) {
  if (coeff === 0) return "";
  const abs = Math.abs(coeff);
  const core = abs === 1 ? symbol : `${abs}${symbol}`;
  if (isFirst) return coeff < 0 ? `-${core}` : core;
  return coeff < 0 ? ` - ${core}` : ` + ${core}`;
}

function buildEquation(a, b, c) {
  let left = "";
  left += term(a, "x", true);
  left += term(b, "y", left.length === 0);
  if (!left) left = "0";
  return `${left} = ${c}`;
}

export function generateTask() {
  let a1 = 1;
  let b1 = 1;
  let c1 = 0;
  let a2 = -1;
  let b2 = 1;
  let c2 = 0;
  let x = 0;
  let y = 0;
  let eliminateVar = "x";

  while (true) {
    x = randInt(-8, 8);
    y = randInt(-8, 8);

    a1 = randInt(-6, 6);
    b1 = randInt(-6, 6);
    if (a1 === 0 || b1 === 0) continue;

    eliminateVar = Math.random() < 0.5 ? "x" : "y";

    if (eliminateVar === "x") {
      a2 = -a1;
      b2 = randInt(-6, 6);
      if (b2 === 0) continue;
      if (b1 + b2 === 0) continue;
    } else {
      b2 = -b1;
      a2 = randInt(-6, 6);
      if (a2 === 0) continue;
      if (a1 + a2 === 0) continue;
    }

    c1 = a1 * x + b1 * y;
    c2 = a2 * x + b2 * y;

    if (Math.abs(c1) > 80 || Math.abs(c2) > 80) continue;
    break;
  }

  const systemLatex = `\\begin{cases} ${buildEquation(a1, b1, c1)} \\\\ ${buildEquation(
    a2,
    b2,
    c2
  )} \\end{cases}`;

  return {
    type: "LinearSystemEliminationTask",
    data: {
      a1,
      b1,
      c1,
      a2,
      b2,
      c2,
      xNum: x,
      xDen: 1,
      yNum: y,
      yDen: 1,
      eliminateVar,
      systemLatex,
    },
  };
}

export function renderTask({ data }) {
  const prompt = data?.neutralPrompt
    ? "Rozwi\u0105\u017c uk\u0142ad r\u00f3wna\u0144:"
    : "Rozwi\u0105\u017c uk\u0142ad r\u00f3wna\u0144 metod\u0105 przeciwnych wsp\u00f3\u0142czynnik\u00f3w:";
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>{prompt}</p>
      <BlockMath math={data.systemLatex} />
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  const xVal = userAnswer?.x || "";
  const yVal = userAnswer?.y || "";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <InlineMath math={"x ="} />
        <FractionInput
          value={xVal}
          onChange={(v) => onChange({ ...userAnswer, x: v })}
          disabled={disabled}
          label="x"
          placeholder="np. 3/2 lub 1.5"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"y ="} />
        <FractionInput
          value={yVal}
          onChange={(v) => onChange({ ...userAnswer, y: v })}
          disabled={disabled}
          label="y"
          placeholder="np. -2/3 lub -0.67"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ux = parseVal(userAnswer?.x);
  const uy = parseVal(userAnswer?.y);
  if (!Number.isFinite(ux) || !Number.isFinite(uy)) return { correct: false };

  const ex = data.xNum / data.xDen;
  const ey = data.yNum / data.yDen;
  const ok = Math.abs(ux - ex) < 0.01 && Math.abs(uy - ey) < 0.01;
  return { correct: ok };
}

export function renderAnswer({ data }) {
  return (
    <div
      className="flex flex-col"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}
    >
      <div style={{ marginBottom: "0.12rem" }}>
        <InlineMath math={`x = ${fracToMixedLatex(data.xNum, data.xDen)}`} />
      </div>
      <div>
        <InlineMath math={`y = ${fracToMixedLatex(data.yNum, data.yDen)}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const xRaw = userAnswer?.x?.raw || "";
  const yRaw = userAnswer?.y?.raw || "";
  if (!xRaw && !yRaw) return <i>(brak odpowiedzi)</i>;

  return (
    <div
      className="flex flex-col"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}
    >
      {xRaw ? (
        <div style={{ marginBottom: "0.12rem" }}>
          <InlineMath math={formatLatexPreview(xRaw, "x")} />
        </div>
      ) : (
        <i>(brak x)</i>
      )}
      {yRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(yRaw, "y")} />
        </div>
      ) : (
        <i>(brak y)</i>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a1, b1, c1, a2, b2, c2, xNum, xDen, yNum, yDen, eliminateVar } = data;
  const sumA = a1 + a2;
  const sumB = b1 + b2;
  const sumC = c1 + c2;

  const xLatex = fracToMixedLatex(xNum, xDen);
  const yLatex = fracToMixedLatex(yNum, yDen);

  const firstEq = buildEquation(a1, b1, c1);
  const secondEq = buildEquation(a2, b2, c2);

  const reducedEq = buildEquation(sumA, sumB, sumC);
  const solvedVar = eliminateVar === "x" ? "y" : "x";
  const solvedValLatex = eliminateVar === "x" ? yLatex : xLatex;

  const substEq =
    eliminateVar === "x"
      ? `${a1}x + ${b1} \\cdot \\left(${yLatex}\\right) = ${c1}`
      : `${a1}\\cdot\\left(${xLatex}\\right) + ${b1}y = ${c1}`;
  const oneVarEq =
    eliminateVar === "x"
      ? `${a1}x = ${c1} ${b1 * (yNum / yDen) >= 0 ? "-" : "+"} ${Math.abs(
          b1 * (yNum / yDen)
        )}`
      : `${b1}y = ${c1} ${a1 * (xNum / xDen) >= 0 ? "-" : "+"} ${Math.abs(
          a1 * (xNum / xDen)
        )}`;
  const oneVarEqSolved =
    eliminateVar === "x" ? `x = ${xLatex}` : `y = ${yLatex}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Dodajemy stronami oba równania (przeciwne współczynniki się redukują):
      </p>
      <BlockMath
        math={String.raw`\begin{cases}
${firstEq}\\
${secondEq}
\end{cases}
\ \Longrightarrow\ ${reducedEq}`}
      />

      <p>
        <strong>Krok 2:</strong> Liczymy <InlineMath math={solvedVar} />:
      </p>
      <BlockMath math={`${solvedVar} = ${solvedValLatex}`} />

      <p>
        <strong>Krok 3:</strong> Podstawiamy do pierwszego równania i wyznaczamy drugą niewiadomą:
      </p>
      <BlockMath math={substEq} />
      <BlockMath math={oneVarEq} />
      <BlockMath math={oneVarEqSolved} />
      <BlockMath math={`x = ${xLatex},\\quad y = ${yLatex}`} />

      <p>
        <strong>Odpowiedź:</strong>{" "}
        <InlineMath math={`\\left(x,y\\right)=\\left(${xLatex}, ${yLatex}\\right)`} />
      </p>
    </div>
  );
}



